import * as React from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { useCallback, useEffect, useRef, useState } from 'react';
import { defaultCoordinates, LISTINGS_PER_PAGE } from '../../const';
import { Cluster, Listing } from '../../graphql/generated';
import { twMerge } from 'tailwind-merge';
import currency from 'currency.js';

interface MapboxMapProps {
  initialOptions?: Omit<mapboxgl.MapboxOptions, 'container'>;
  className?: string;
  searchButtonClassName?: string;
  onMapLoaded?(map: mapboxgl.Map): void;
  onMapRemoved?(): void;
  onMapBoundsChanged?(
    lastBounds:
      | mapboxgl.LngLatBounds
      | {
          _sw: { lng: number; lat: number };
          _ne: { lng: number; lat: number };
        },
  ): void;
  onMapBoundUpdated?(bounds: mapboxgl.LngLatBounds): void;
  onMarkerClick?(event: MouseEvent): void;
  data?: {
    center: [number, number];
    listings: Listing[];
    polygons?: any[];
    mapBounds?: any;
    clusters: Cluster[];
  };
  activeListing: Listing;
  isFiltersVisible: boolean;
}

let renderedMarkers = [];

function renderPolygons(mapboxMap: mapboxgl.Map, polygons: []) {
  // Add a data source containing GeoJSON data.
  mapboxMap.addSource('polygon', {
    type: 'geojson',
    data: {
      type: 'Feature',
      geometry: {
        type: 'Polygon',
        coordinates: [polygons],
      },
      properties: {},
    },
  });

  // Add a new layer to visualize the polygon.
  mapboxMap.addLayer({
    id: 'polygon',
    type: 'fill',
    source: 'polygon', // reference the data source
    layout: {},
    paint: {
      'fill-color': '#f6cbab', // blue color fill
      'fill-opacity': 0.5,
    },
  });
  // Add a black outline around the polygon.
  mapboxMap.addLayer({
    id: 'outline',
    type: 'line',
    source: 'polygon',
    layout: {},
    paint: {
      'line-color': '#f8a061',
      'line-width': 3,
    },
  });
}

function renderMarkers(
  clusters: Cluster[],
  mapInstance,
  setClusterMarkers: (
    value:
      | ((prevState: mapboxgl.Marker[]) => mapboxgl.Marker[])
      | mapboxgl.Marker[],
  ) => void,
  listings: Listing[],
  listingsForRendering: any[],
  onMarkerClick: (event: MouseEvent) => void,
  setMarkers: (
    value:
      | ((prevState: mapboxgl.Marker[]) => mapboxgl.Marker[])
      | mapboxgl.Marker[],
  ) => void,
  onMapBoundsChanged,
) {
  if (!mapInstance) return;
  // count listing in all clusters
  const totalListings = clusters.reduce(
    (acc, cluster) => acc + cluster.count,
    0,
  );

  if (totalListings < LISTINGS_PER_PAGE) {
    if (mapInstance.getSource('listings-src')) {
      mapInstance
        .getSource('listings-src')
        .setData(convertListingsToGeoJson(listings));
    } else {
      mapInstance.addSource('listings-src', {
        type: 'geojson',
        data: convertListingsToGeoJson(listings),
        cluster: true,
        clusterRadius: 80,
      });
    }

    if (!mapInstance.getLayer('clusters')) {
      mapInstance.addLayer({
        id: 'clusters',
        type: 'circle',
        source: 'listings-src',
        filter: ['has', 'point_count'],
        paint: {
          'circle-color': [
            'step',
            ['get', 'point_count'],
            '#098080',
            100,
            '#098080',
            750,
            '#098080',
          ],
          'circle-radius': [
            'step',
            ['get', 'point_count'],
            20,
            100,
            30,
            750,
            40,
          ],
        },
      });
    }

    // check that the layer is not already added
    if (!mapInstance.getLayer('cluster-count')) {
      mapInstance.addLayer({
        id: 'cluster-count',
        type: 'symbol',
        source: 'listings-src',
        filter: ['has', 'point_count'],
        layout: {
          'text-field': '{point_count_abbreviated}',
          'text-size': 13,
          'text-font': ['Open Sans Bold', 'Arial Unicode MS Regular'],
        },
        paint: {
          'text-color': '#ffffff',
        },
      });
    }

    // render custom markers for each unclustered point on map moveend
    mapInstance.on('moveend', () => {
      if (!mapInstance) {
        return;
      }

      renderedMarkers.forEach((marker) => {
        marker.remove();
      });
      renderedMarkers = [];
      listingsForRendering = [];

      const pointFeatures = mapInstance
        .querySourceFeatures('listings-src')
        .filter((feature) => !feature.properties.cluster);

      // console.log('pointFeatures', JSON.stringify(pointFeatures, null, 2));

      pointFeatures.forEach((feature) => {
        const listing = JSON.parse(feature.properties.listing);
        listingsForRendering.push(listing);
      });

      // keep only unique listings by mlsNumber
      listingsForRendering = listingsForRendering.filter(
        (listing, index, self) =>
          index === self.findIndex((t) => t.mlsNumber === listing.mlsNumber),
      );

      const newMarkers = renderListingMarkers(
        listingsForRendering,
        onMarkerClick,
        mapInstance,
      );
      renderedMarkers.push(...newMarkers);
    });
  } else {
    for (const cluster of clusters) {
      const el = document.createElement('div');
      el.className = `marker cluster-tag`;
      el.id = `cluster-${clusters.indexOf(cluster)}`;
      el.attributes['data-lat'] = cluster.location.latitude;
      el.attributes['data-lng'] = cluster.location.longitude;
      el.attributes['data-sw'] = {
        lng: +cluster.bounds.bottom_right.longitude,
        lat: +cluster.bounds.bottom_right.latitude,
      };
      el.attributes['data-ne'] = {
        lng: +cluster.bounds.top_left.longitude,
        lat: +cluster.bounds.top_left.latitude,
      };
      el.innerHTML = `${cluster.count}`;

      if (cluster.count > 1) {
        el.addEventListener('click', (event) => {
          const sw = (event.target as HTMLElement).attributes['data-sw'];
          const ne = (event.target as HTMLElement).attributes['data-ne'];
          onMapBoundsChanged({
            _sw: {
              lng: sw.lng,
              lat: sw.lat,
            },
            _ne: {
              lng: ne.lng,
              lat: ne.lat,
            },
          });
        });

        // Add markers to the map.
        const newClusterMarker = new mapboxgl.Marker(el)
          .setLngLat({
            lng: Number(cluster.location.longitude),
            lat: Number(cluster.location.latitude),
          })
          .addTo(mapInstance);

        setClusterMarkers((prevClusterMarkers) => [
          ...prevClusterMarkers,
          newClusterMarker,
        ]);
      } else {
        // If the cluster has only one listing, render the listing marker
        // but only if it exists in the listings array
        const clusterListing = listings.find(
          (listing) => listing.mlsNumber === cluster.listing.mlsNumber,
        );
        if (clusterListing) {
          listingsForRendering.push(clusterListing);
        }
      }
    }

    const newMarkers = renderListingMarkers(
      listingsForRendering,
      onMarkerClick,
      mapInstance,
    );

    setMarkers((prevMarkers) => [...prevMarkers, ...newMarkers]);
  }
}

function renderListingMarkers(
  listingsForRendering: any[],
  onMarkerClick: (event: MouseEvent) => void,
  mapInstance,
) {
  const newMarkers = [];
  for (const listing of listingsForRendering) {
    const el = document.createElement('div');
    el.className = `marker price-tag`;
    el.id = `marker-${listing.mlsNumber}`;
    el.attributes['data-lat'] = listing.map.latitude;
    el.attributes['data-lng'] = listing.map.longitude;
    el.attributes['data-listing-id'] = listing.mlsNumber;
    el.innerHTML = `${currency(listing.listPrice, {
      precision: 0,
    }).format()}`;

    el.addEventListener('click', (event) => {
      if (onMarkerClick) {
        onMarkerClick(event);
      }
    });

    // Add markers to the map.
    const newMarker = new mapboxgl.Marker(el)
      .setLngLat({
        lng: Number(listing.map.longitude),
        lat: Number(listing.map.latitude),
      })
      .addTo(mapInstance);

    newMarkers.push(newMarker);
  }

  return newMarkers;
}

function convertListingsToGeoJson(listings: Listing[]): any {
  return {
    type: 'FeatureCollection',
    features: listings.map((listing) => ({
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [listing.map.longitude, listing.map.latitude],
      },
      properties: {
        listing,
      },
    })),
  };
}

function renderMapBoxComponent(
  mapNode: React.MutableRefObject<null>,
  center: {
    lng: number;
    lat: number;
  },
  mapBounds: any,
  initialOptions: Omit<mapboxgl.MapboxOptions, 'container'>,
  setMapInstance: (value: unknown) => void,
  polygons: any[],
  onMapLoaded: (map: mapboxgl.Map) => void,
  onMapRemoved: () => void,
) {
  const node = mapNode.current;
  if (typeof window === 'undefined' || node === null) return;

  const mapboxMap = new mapboxgl.Map({
    container: node,
    accessToken: process.env.NEXT_PUBLIC_MAPBOX_ACCESS_TOKEN,
    style: 'mapbox://styles/mapbox/streets-v11',
    center: center,
    zoom: 11,
    bounds: mapBounds,
    ...initialOptions,
  });

  mapboxMap.on('load', (mapBoxEvent) => {
    setMapInstance(mapBoxEvent.target);
  });

  return () => {
    mapboxMap.remove();
    if (onMapRemoved) {
      onMapRemoved();
    }
  };
}

function removeAllMarkers(
  clusterMarkers: mapboxgl.Marker[],
  setClusterMarkers: (
    value:
      | ((prevState: mapboxgl.Marker[]) => mapboxgl.Marker[])
      | mapboxgl.Marker[],
  ) => void,
  markers: mapboxgl.Marker[],
  setMarkers: (
    value:
      | ((prevState: mapboxgl.Marker[]) => mapboxgl.Marker[])
      | mapboxgl.Marker[],
  ) => void,
) {
  clusterMarkers.forEach((marker) => marker.remove());
  setClusterMarkers([]);
  markers.forEach((marker) => marker.remove());
  setMarkers([]);
}

export default function MapBox({
  initialOptions = {},
  onMapLoaded,
  onMapRemoved,
  onMapBoundUpdated,
  onMarkerClick,
  onMapBoundsChanged,
  className,
  searchButtonClassName,
  activeListing,
  isFiltersVisible,
  ...rest
}: MapboxMapProps) {
  const {
    center = defaultCoordinates,
    listings,
    polygons,
    mapBounds,
    clusters,
  } = rest.data;

  const [lastActiveListing, setLastActiveListing] = useState<Listing>(null);
  const [mapInstance, setMapInstance] = useState(null);
  const [clusterMarkers, setClusterMarkers] = useState<mapboxgl.Marker[]>([]);
  const [markers, setMarkers] = useState<mapboxgl.Marker[]>([]);
  const boundChangeHandler = useRef(onMapBoundsChanged);

  const mapNode = useRef(null);

  const renderListings = useCallback(() => {
    if (!mapInstance) return;

    const listingsForRendering = [];

    // Remove all existing markers
    removeAllMarkers(clusterMarkers, setClusterMarkers, markers, setMarkers);

    if (mapBounds && mapBounds.getCenter) {
      // fitBounds only if the delta between the current bounds and the new bounds is greater than 0.5
      const delta = mapInstance
        .getBounds()
        .getCenter()
        .distanceTo(mapBounds.getCenter());

      if (delta > 0.5) {
        mapInstance['isLoading'] = true;
        mapInstance.fitBounds(mapBounds);
      }
    } else {
      if (
        mapInstance.getCenter &&
        mapInstance.getCenter().distanceTo(center) > 0.5
      ) {
        mapInstance['isLoading'] = true;
        mapInstance.jumpTo({
          center: center,
        });
      }
    }

    renderMarkers(
      clusters,
      mapInstance,
      setClusterMarkers,
      listings,
      listingsForRendering,
      onMarkerClick,
      setMarkers,
      onMapBoundsChanged,
    );
  }, [listings, mapInstance]);

  useEffect(() => {
    if (mapInstance) {
      renderListings();
    }
  }, [mapInstance, renderListings, listings]);

  useEffect(
    () =>
      renderMapBoxComponent(
        mapNode,
        center as { lng: number; lat: number },
        mapBounds,
        initialOptions,
        setMapInstance,
        polygons,
        onMapLoaded,
        onMapRemoved,
      ),
    [],
  );

  useEffect(() => {
    if (!mapInstance) {
      return;
    }

    mapInstance.on('moveend', (e) => {
      const bounds = mapInstance.getBounds() as any;

      if (boundChangeHandler.current && mapInstance['isLoading'] !== true) {
        boundChangeHandler.current(bounds);
      } else if (mapInstance['isLoading'] === true) {
        mapInstance['isLoading'] = false;
      }
    });

    if (onMapLoaded) {
      mapInstance.once('load', (ev) => {
        onMapLoaded(mapInstance);
      });
    }
  }, [mapInstance, onMapLoaded]);

  // Add activeMarker class to the div element with data-listing-id equal to the activeListing.mlsNumber
  useEffect(() => {
    const node = mapNode.current;
    if (typeof window === 'undefined' || node === null) return;

    if (activeListing) {
      // add activeMarker class to the div element with data-listing-id equal to the activeListing.mlsNumber
      const el = node.querySelector(`#marker-${activeListing.mlsNumber}`);
      if (!el) return;
      if (lastActiveListing) {
        const lastEl = node.querySelector(
          `#marker-${lastActiveListing.mlsNumber}`,
        );
        lastEl.classList.remove('activeMarker');
      }
      el.classList.add('activeMarker');
      setLastActiveListing(activeListing);
    } else {
      const el = node.querySelector(`#marker-${lastActiveListing?.mlsNumber}`);
      if (!el) return;
      el.classList.remove('activeMarker');
      setLastActiveListing(null);
    }
  }, [activeListing, lastActiveListing]);

  return (
    <div className={twMerge('w-full h-full relative', className)}>
      <div ref={mapNode} style={{ width: '100%', height: '100%' }} />
    </div>
  );
}
